import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Typography } from "@material-tailwind/react";
import { ShieldCheckIcon, ShieldExclamationIcon } from "@heroicons/react/24/solid";
import { api } from "../../../../services/api/api";
import { ApiErrorLike } from "../../../../services/api/error";
import {
  SafetyDiagRealizationHistoryEntry,
  SafetyDiagReport,
} from "../../../../types/safety-diag/safetyDiagReport";
import SafetyDiagStepTimeline from "./SafetyDiagStepTimeline";
import SafetyDiagMediaImage, { safetyDiagMediaPath } from "./SafetyDiagMediaImage";
import SafetyDiagExportButton from "./SafetyDiagExportButton";
import { formatApiDateTime } from "../../../../services/utils/utils";

/* Styles */
import "./SafetyDiagReportSection.scss";

/**
 * "Diagnostic sécurité" section on the work-order detail page. Fetches the localized
 * safety-diag report and renders the outcome, ordered timeline, signatures, overrides,
 * plus a PDF export. Shows a clean empty state when the WO has no sealed realization.
 *
 * An intervention can carry several diagnoses — a new one run while the work is under way, a
 * terminal KO left by a withdrawal right, one retired when the job changed hands. The default stays
 * what it always was, the most recently sealed; a picker appears only when there is more than one to
 * pick from, so a single-diagnosis intervention looks exactly as it did (ClickUp 86cbcf4ne).
 */
export default function SafetyDiagReportSection({
  workOrderId,
  workOrderRef,
  customer,
}: {
  workOrderId: string;
  workOrderRef?: string;
  customer?: EasyTech.Customer;
}) {
  const { i18n, t } = useTranslation("diagSecu");
  const [report, setReport] = useState<SafetyDiagReport | null>(null);
  const [history, setHistory] = useState<SafetyDiagRealizationHistoryEntry[]>([]);
  const [customerLanguages, setCustomerLanguages] = useState<any[]>([]);
  // null = whichever the server considers current. Kept distinct from "the newest id" so the
  // default path stays the single work-order call it has always been.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    if (customer?.languages && Array.isArray(customer.languages) && customer.languages.length > 0) {
      setCustomerLanguages(customer.languages);
      return;
    }
    if (customer?.id) {
      api
        .get(`users/customers/${customer.id}`)
        .then((data: any) => {
          if (active && Array.isArray(data?.languages)) {
            setCustomerLanguages(data.languages);
          }
        })
        .catch(() => {
          if (active) setCustomerLanguages([]);
        });
    } else {
      setCustomerLanguages([]);
    }
    return () => {
      active = false;
    };
  }, [customer?.id, customer?.languages]);

  useEffect(() => {
    let active = true;
    setSelectedId(null);
    setHistory([]);

    // Best-effort: the picker is an enhancement, so a failure here must not take the report with
    // it. On older backends this 404s and the section simply behaves as it did before.
    api
      .get(`admin/safety-diag/work-orders/${workOrderId}/realizations`)
      .then((data) => {
        // Guarded rather than cast: api.get returns unknown and nothing validates the shape, so an
        // older backend answering something else must degrade to "no picker", never throw and take
        // the report down with it.
        if (active) {
          setHistory(Array.isArray(data) ? (data as SafetyDiagRealizationHistoryEntry[]) : []);
        }
      })
      .catch(() => {
        if (active) setHistory([]);
      });

    return () => {
      active = false;
    };
  }, [workOrderId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    setError(false);

    const lang = (i18n.language || "en").split("-")[0];
    const path = selectedId
      ? `admin/safety-diag/work-orders/${workOrderId}/realizations/${selectedId}/report?lang=${lang}`
      : `admin/safety-diag/work-orders/${workOrderId}/report?lang=${lang}`;
    api
      .get(path)
      .then((data) => {
        if (active) setReport(data as SafetyDiagReport);
      })
      .catch((err: ApiErrorLike) => {
        if (!active) return;
        if (err?.status === 404) setNotFound(true);
        else setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [workOrderId, selectedId, i18n.language]);

  // Only diagnoses that reached an outcome can be opened; a draft has no report to render. One
  // sealed diagnosis is the ordinary case, and it must look exactly as it did before, so the picker
  // appears only when there is genuinely a choice to make.
  const openable = history.filter((entry) => entry.sealed);
  const picker =
    openable.length > 1 ? (
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 pb-3 pt-1">
        <label htmlFor="safety-diag-history" className="text-sm font-medium text-gray-600">
          {t("diagSecu:report:historyLabel")}
        </label>
        <select
          id="safety-diag-history"
          aria-label={t("diagSecu:report:historySelectAria")}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-none focus:border-[#41A1FF] focus:outline focus:outline-1 focus:outline-[#41A1FF]"
          value={selectedId ?? openable[openable.length - 1].realizationId}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          {/* Newest first: the current state of the intervention is what a planner opens the tab for. */}
          {[...openable].reverse().map((entry) => (
            <option key={entry.realizationId} value={entry.realizationId}>
              {t("diagSecu:report:historyOptionSealed", {
                n: entry.attempt,
                date: formatApiDateTime(entry.sealedAt ?? entry.startedAt),
              })}
              {entry.governing ? ` (${t("diagSecu:report:historyGoverning")})` : ""}
            </option>
          ))}
        </select>
      </div>
    ) : null;

  if (loading) {
    return (
      <div className="safety-diag-section workorder-information">
        {picker}
        <div className="flex items-center justify-center p-8">
          <Typography className="text-gray-600">{t("diagSecu:report:loading")}</Typography>
        </div>
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="safety-diag-section workorder-information">
        {picker}
        <div className="flex items-center justify-center p-8">
          <Typography className="text-gray-500">{t("diagSecu:report:empty")}</Typography>
        </div>
      </div>
    );
  }
  if (error || !report) {
    return (
      <div className="safety-diag-section workorder-information">
        {picker}
        <div className="flex items-center justify-center p-8">
          <Typography className="text-red-600">{t("diagSecu:report:loadError")}</Typography>
        </div>
      </div>
    );
  }

  const isOk = report.issue === "OK";
  const current = history.find((entry) => entry.realizationId === report.realizationId);

  return (
    <div className="safety-diag-section workorder-information">
      {picker}

      {/* Header + export */}
      <div className="flex justify-between items-center">
        <div className="information-header">
          <div>
            <Typography variant="h4">{t("diagSecu:report:title")}</Typography>
            <p className="text-sm text-gray-500 mt-1">
              {/* Spelled out because "Version 12" below is the flow version, not the attempt count. */}
              {openable.length > 1 && current && (
                <>
                  {t("diagSecu:report:historyAttempt", {
                    n: current.attempt,
                    total: openable.length,
                  })}{" "}
                  ·{" "}
                </>
              )}
              {report.flowCode} · {t("diagSecu:report:version")} {report.versionNumber}
              {report.versionDeprecated && (
                <span className="ml-2 rounded bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800">
                  {t("diagSecu:report:deprecatedVersion")}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex gap-2 mr-4">
          <SafetyDiagExportButton
            workOrderId={workOrderId}
            workOrderRef={workOrderRef}
            customer={customer}
            customerLanguages={customerLanguages}
            languageIsoCode={report.language ?? undefined}
            realizationId={selectedId ?? undefined}
            attemptLabel={
              openable.length > 1 && current ? String(current.attempt) : undefined
            }
          />
        </div>
      </div>

      {/* Informations générales dans une Card avec grille */}
      <Card className="information-content">
        {/* Résultat (Outcome) */}
        <div className="field">
          <label>{t("diagSecu:report:outcome")}</label>
          <div className="flex items-center gap-2">
            {report.issue == null ? (
              <Typography className="text-gray-700 font-normal">{t("diagSecu:report:noOutcome")}</Typography>
            ) : isOk ? (
              <span className="flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800 w-fit">
                <ShieldCheckIcon className="h-4 w-4" />
                {t(`diagSecu:report:issue:${report.issue}`)}
              </span>
            ) : (
              <span className="flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-800 w-fit">
                <ShieldExclamationIcon className="h-4 w-4" />
                {t(`diagSecu:report:issue:${report.issue}`)}
              </span>
            )}
          </div>
        </div>

        {/* Référence */}
        <div className="field">
          <label>{t("diagSecu:report:reference")}</label>
          <Typography className="text-gray-900 font-normal">{report.workOrderRef ?? workOrderRef ?? "—"}</Typography>
        </div>

        {/* Statut */}
        <div className="field">
          <label>{t("diagSecu:report:status")}</label>
          <Typography className="text-gray-900 font-normal">{report.status}</Typography>
        </div>

        {/* Technicien */}
        <div className="field">
          <label>{t("diagSecu:report:technician")}</label>
          <Typography className="text-gray-900 font-normal">{report.technicianName ?? report.technicianId ?? "—"}</Typography>
        </div>

        {/* Date de début */}
        <div className="field">
          <label>{t("diagSecu:report:startedAt")}</label>
          <Typography className="text-gray-900 font-normal">{formatApiDateTime(report.startedAt)}</Typography>
        </div>

        {/* Date de clôture */}
        <div className="field">
          <label>{t("diagSecu:report:sealedAt")}</label>
          <Typography className="text-gray-900 font-normal">{formatApiDateTime(report.sealedAt)}</Typography>
        </div>

        {/* Date de signature */}
        {report.signedAt && (
          <div className="field">
            <label>{t("diagSecu:report:signedAt")}</label>
            <Typography className="text-gray-900 font-normal">{formatApiDateTime(report.signedAt)}</Typography>
          </div>
        )}

        {/* Motif du droit de retrait */}
        {report.withdrawalReason && (
          <div className="field col-span-1 md:col-span-2">
            <label>{t("diagSecu:report:reason")}</label>
            <Typography className="text-red-700 font-medium">
              {report.withdrawalReason}
            </Typography>
          </div>
        )}
      </Card>

      {/* Étapes du diagnostic */}
      <div className="information-header">
        <Typography variant="h4">{t("diagSecu:report:steps")}</Typography>
      </div>

      <Card className="p-4">
        {!report.pathRecorded && (
          <p className="mb-4 rounded bg-yellow-50 p-3 text-sm text-yellow-800">
            {t("diagSecu:report:pathNotRecorded")}
          </p>
        )}
        <SafetyDiagStepTimeline steps={report.steps} workOrderId={workOrderId} />
      </Card>

      {/* Signatures */}
      {report.signatures.length > 0 && (
        <>
          <div className="information-header">
            <Typography variant="h4">{t("diagSecu:report:signatures")}</Typography>
          </div>
          <Card className="p-4">
            <div className="flex flex-wrap gap-6">
              {report.signatures.map((sig) => (
                <div
                  key={sig.signatureId}
                  className="flex flex-col items-center p-3 rounded-lg border border-gray-200 bg-white shadow-xs"
                >
                  <SafetyDiagMediaImage
                    downloadPath={safetyDiagMediaPath(workOrderId, sig.signatureId)}
                    alt={sig.role}
                    modalTitle={
                      sig.signatoryName
                        ? `${sig.signatoryName} (${t(`diagSecu:report:role:${sig.role}`)})`
                        : t(`diagSecu:report:role:${sig.role}`)
                    }
                    previewable
                    className="h-28 w-56 rounded border border-gray-200 bg-white object-contain p-1"
                  />
                  <Typography variant="small" className="mt-2 font-medium text-gray-900">
                    {t(`diagSecu:report:role:${sig.role}`)}
                  </Typography>
                  {sig.signatoryName && (
                    <Typography variant="small" className="text-gray-700 font-normal">
                      {sig.signatoryName}
                    </Typography>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Dérogations */}
      {report.overrides.length > 0 && (
        <>
          <div className="information-header">
            <Typography variant="h4">{t("diagSecu:report:overrides")}</Typography>
          </div>
          <Card className="p-4 flex flex-col gap-3">
            {report.overrides.map((ov) => (
              <div key={ov.overrideId} className="rounded-lg border border-gray-200 p-3 text-sm bg-white">
                <p className="text-gray-700 font-medium">
                  {t("diagSecu:report:overrideBy")} {ov.byUserId ?? "—"} · {formatApiDateTime(ov.createdAt)}
                </p>
                <Typography className="mt-1 text-gray-900 font-normal">{ov.justification}</Typography>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}
